// attorneys_front/src/components/pages/MarketingClaimVendorProfilePage.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../api';
import { useNotification } from '../../context/NotificationContext';
import Loader from '../Loader';
import SignUpVideo from '../User/SignUpVideo';
import ToggleBillingPeriodNew from '../../components/Form/Input/ToggleBillingPeriodNew';
import SmsConsentCheckbox from '../Form/SmsConsentCheckbox';

const MarketingClaimVendorProfilePage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [vendorData, setVendorData] = useState(null);
    const [planData, setPlanData] = useState(null);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirm_password: '',
        sms_notifications_consent: false,
        sms_marketing_consent: false,
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [billingPeriod, setBillingPeriod] = useState('monthly');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const styles = {
        pageContainer: {
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            height: isMobile ? 'auto' : '100vh',
            width: '100%',
            overflow: isMobile ? 'auto' : 'hidden',
        },
        videoWrapper: {
            flex: '0 0 50%',
            position: 'relative',
            height: '100vh',
            overflow: 'hidden',
            display: isMobile ? 'none' : 'block',
        },
        contentContainer: {
            flex: 1,
            height: isMobile ? 'auto' : '100vh',
            backgroundColor: '#f4f7f9',
            padding: isMobile ? '20px' : '40px',
            overflowY: 'auto',
            boxSizing: 'border-box',
        },
        notificationBlock: {
            padding: '16px 20px',
            maxWidth: '700px',
            width: '100%',
            backgroundColor: '#fff',
            border: '1px solid #0d6efd',
            color: '#0a58ca',
            borderRadius: '8px',
            margin: '0 auto 25px auto',
            textAlign: 'center',
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: '500',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        },
        infoBlock: {
            padding: isMobile ? '20px' : '30px 40px',
            maxWidth: '700px',
            width: '100%',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 6px 18px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            margin: '0 auto',
        },
        heading: {
            borderBottom: '1px solid #eee',
            paddingBottom: '15px',
            marginBottom: '25px',
            color: '#333',
            fontSize: isMobile ? '22px' : '26px',
            fontWeight: '600',
            textAlign: 'center',
        },
        subHeading: {
            fontSize: isMobile ? '16px' : '18px',
            color: '#555',
            marginBottom: '20px',
            textAlign: 'center',
            lineHeight: 1.5,
        },
        infoSection: {
            marginBottom: '25px',
            paddingBottom: '20px',
            borderBottom: '1px solid #f0f0f0',
        },
        infoSectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#444',
            marginBottom: '15px',
            borderBottom: '1px solid #eee',
            paddingBottom: '10px',
        },
        infoRow: {
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            marginBottom: '12px',
            fontSize: '16px',
            alignItems: isMobile ? 'flex-start' : 'center',
        },
        infoLabel: { fontWeight: 'bold', color: '#444' },
        infoValue: {
            color: '#666',
            textAlign: isMobile ? 'left' : 'right',
            marginTop: isMobile ? '4px' : 0,
        },
        formGroup: { marginBottom: '15px', textAlign: 'left'},
        input: { width: '100%', padding: '12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' },
        errorText: { color: '#d9534f', fontSize: '12px', marginTop: '4px' },
        buttonContainer: { marginTop: '30px', textAlign: 'center' },
        proceedButton: { padding: '12px 25px', fontSize: '16px', fontWeight: 'bold', color: '#fff', backgroundColor: '#007bff', border: 'none', borderRadius: '5px', cursor: 'pointer', transition: 'background-color 0.3s ease'},
        errorBox: { padding: '20px', borderRadius: '5px', marginBottom: '20px', textAlign: 'center', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24' },
        trialNotification: {
            marginTop: '20px',
            padding: '15px',
            border: '1px solid #0d6efd',
            borderRadius: '5px',
            backgroundColor: '#cfe2ff',
            fontSize: '1em',
            color: '#0a58ca',
            lineHeight: '1.4',
            textAlign: 'center',
        },
        termsText: {
            fontSize: '14px',
            color: '#666',
            marginTop: '20px',
            textAlign: 'center',
            lineHeight: '1.5',
        },
        termsLink: {
            color: '#007bff',
            textDecoration: 'none',
        },
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!token) {
            setError("No activation token provided.");
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const vendorResponse = await api.get(`/marketing-app/claim-vendor-info/${token}/`, { withoutAuth: true });
                const fetchedVendorData = vendorResponse.data;
                setVendorData(fetchedVendorData);

                const serviceTypeId = fetchedVendorData?.service_type?.service_type_id;
                if (!serviceTypeId) {
                    throw new Error("Service Type ID not found in vendor data.");
                }

                const planResponse = await api.get(`/tariff-services/?service_type_id=${serviceTypeId}`, { withoutAuth: true });
                const activePlan = (planResponse.data || []).find(p => p.status === 'active');

                if (activePlan) {
                    setPlanData(activePlan);
                } else {
                    throw new Error("No active subscription plan found for this vendor's service type.");
                }

            } catch (err) {
                const errorMsg = err.response?.data?.error || err.response?.data?.detail || "Could not load profile information. The link may be invalid or expired.";
                setError(errorMsg);
                showNotification("Error", errorMsg, null, "error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [token, showNotification]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.first_name.trim()) errors.first_name = "First name is required.";
        if (!formData.last_name.trim()) errors.last_name = "Last name is required.";
        if (!formData.email.trim()) errors.email = "Email is required.";
        else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = "Invalid email address.";
        if (!formData.password) errors.password = "Password is required.";
        else if (formData.password.length < 8) errors.password = "Password must be at least 8 characters long.";
        if (formData.password !== formData.confirm_password) errors.confirm_password = "Passwords do not match.";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            showNotification("Validation Error", "Please correct the errors in the form.", null, "error");
            return;
        }

        setIsSubmitting(true);

        const payload = {
            ...formData,
            token: token,
            billing_period: billingPeriod,
        };

        try {
            const response = await api.post('/marketing-app/initiate-vendor-claim/', payload, { withoutAuth: true });
            const checkoutUrl = response.data?.checkout_url;
            if (checkoutUrl) {
                showNotification("Success", "Redirecting to secure payment...", null, "success");
                window.location.href = checkoutUrl;
            } else {
                throw new Error("Checkout URL was not provided by the server.");
            }
        } catch (err) {
            const errorDetail = err.response?.data?.detail;
            const errorMessage = errorDetail || "An unexpected error occurred. Please try again.";
            showNotification("Submission Error", errorMessage, null, "error");
            setIsSubmitting(false);
        }
    };

    if (loading) return <Loader message="Loading Vendor Profile and Plan Information..." />;
    if (error) return <div style={{...styles.infoBlock, ...styles.errorBox, margin: '40px auto'}}><h2 style={{color: '#721c24', marginTop: 0}}>Error Loading Page</h2><p>{error}</p></div>;

    const trialPrice = 1;
    const buttonText = isSubmitting ? 'Processing...' : `Start Trial: $${trialPrice.toFixed(2)} for 90 Days`;
    const canProceed = !isSubmitting && !!vendorData && !!planData;

    return (
        <div style={styles.pageContainer}>
            <div style={styles.videoWrapper}>
                <SignUpVideo />
            </div>
            <div style={styles.contentContainer}>
                <div style={styles.notificationBlock}>
                    Complete your details and attract new clients — only $1 for 3 months!
                </div>

                {vendorData && (
                    <div style={styles.infoBlock}>
                        <h1 style={styles.heading}>Claim Your Vendor Profile: {vendorData.name}</h1>

                        <div style={styles.infoSection}>
                            <h2 style={styles.infoSectionTitle}>Vendor Details</h2>
                            <div style={styles.infoRow}><span style={styles.infoLabel}>Email:</span><span style={styles.infoValue}>{vendorData.email || 'N/A'}</span></div>
                            <div style={styles.infoRow}><span style={styles.infoLabel}>Phone:</span><span style={styles.infoValue}>{vendorData.phone_number || 'N/A'}</span></div>
                            <div style={styles.infoRow}><span style={styles.infoLabel}>Service Type:</span><span style={styles.infoValue}>{vendorData.service_type?.service_type_name || 'N/A'}</span></div>
                        </div>

                        <h2 style={{...styles.heading, fontSize: '22px', marginTop: '10px' }}>Create Your Account</h2>
                        <p style={styles.subHeading}>Create an account to manage this profile. After successful payment of ${trialPrice.toFixed(2)}, this profile will be linked to your new account.</p>

                        <form onSubmit={handleSubmit} noValidate>
                            {/* Form inputs similar to MarketingClaimProfilePage */}
                            <div style={{display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '15px'}}>
                                <div style={{...styles.formGroup, flex: 1}}>
                                    <input name="first_name" type="text" placeholder="First Name" style={styles.input} value={formData.first_name} onChange={handleChange} required />
                                    {formErrors.first_name && <p style={styles.errorText}>{formErrors.first_name}</p>}
                                </div>
                                <div style={{...styles.formGroup, flex: 1}}>
                                    <input name="last_name" type="text" placeholder="Last Name" style={styles.input} value={formData.last_name} onChange={handleChange} required />
                                    {formErrors.last_name && <p style={styles.errorText}>{formErrors.last_name}</p>}
                                 </div>
                            </div>
                            <div style={styles.formGroup}>
                                <input name="email" type="email" placeholder="Email Address" style={styles.input} value={formData.email} onChange={handleChange} required />
                                {formErrors.email && <p style={styles.errorText}>{formErrors.email}</p>}
                            </div>
                            <div style={styles.formGroup}>
                                <input name="password" type="password" placeholder="Password (min. 8 characters)" style={styles.input} value={formData.password} onChange={handleChange} required />
                                {formErrors.password && <p style={styles.errorText}>{formErrors.password}</p>}
                            </div>
                             <div style={styles.formGroup}>
                                <input name="confirm_password" type="password" placeholder="Confirm Password" style={styles.input} value={formData.confirm_password} onChange={handleChange} required />
                                {formErrors.confirm_password && <p style={styles.errorText}>{formErrors.confirm_password}</p>}
                            </div>

                            <div style={styles.subscriptionSection}>
                                <h2 style={{...styles.infoSectionTitle, textAlign: 'center', borderBottom: 'none'}}>Choose Your Plan</h2>
                                {planData ? (
                                    <>
                                        <ToggleBillingPeriodNew
                                            billingPeriod={billingPeriod}
                                            onBillingPeriodChange={setBillingPeriod}
                                            disabled={isSubmitting}
                                            monthlyPrice={planData.monthly_price}
                                            annualPrice={planData.annual_price}
                                            effectiveMonthlyPrice={planData.annual_price ? (parseFloat(planData.annual_price) / 12).toFixed(0) : null}
                                        />
                                        <div style={styles.trialNotification}>
                                            <strong>Important:</strong> You will be charged <strong>${trialPrice.toFixed(2)} today</strong> for a <strong>90-day trial</strong> of the Pro Plan. After the trial, your selected plan
                                            ({billingPeriod === 'monthly' ? `$${planData.monthly_price}/month` : `$${planData.annual_price}/year, billed annually`})
                                            will begin automatically. You can cancel anytime.
                                        </div>
                                    </>
                                ) : (
                                    <p>Loading plan details...</p>
                                )}
                            </div>

                            <div style={styles.termsText}>
                                By clicking "{buttonText}", you agree to charge ${trialPrice.toFixed(2)} for the trial and to our <br/>
                                <Link to="/terms-of-use" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>Terms of Use</Link> and {' '}
                                <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>Privacy Policy</Link>.
                            </div>

                            <SmsConsentCheckbox
                                name="sms_notifications_consent"
                                checked={formData.sms_notifications_consent}
                                onChange={handleChange}
                                style={{marginTop: '15px'}}
                            />
                            <SmsConsentCheckbox
                                name="sms_marketing_consent"
                                checked={formData.sms_marketing_consent}
                                onChange={handleChange}
                                style={{marginTop: '15px'}}
                                variant="marketing"
                            />


                            <div style={styles.buttonContainer}>
                                <button type="submit" style={{...styles.proceedButton, ...(!canProceed && {backgroundColor: '#ccc', cursor: 'not-allowed'})}} disabled={!canProceed}>
                                    {buttonText}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MarketingClaimVendorProfilePage;
